import { FlowRef, FlowStage } from './FlowStrip';
import { PERMISSIONS as P } from '../constants/permissions';

/**
 * Builders for the FlowStrip of each detail page. They use only ids the page's own record already holds, so a stage
 * links only when the record really links to that document. Anything else is plain text.
 * The permission lists are the same keys the target page's route requires (see App.tsx).
 */

const WO_VIEW = [P.MAINTENANCE_WO_VIEW_ALL, P.MAINTENANCE_WO_WORK, P.MAINTENANCE_WO_MANAGE];
const REQ_VIEW = [P.REQUISITION_ISSUE, P.REQUISITION_APPROVE];

const ref = (text: string | null | undefined, to: string | null | undefined): FlowRef[] => (text ? [{ text, to: to ?? null }] : []);
const at = (path: string, id?: string | null) => (id ? `${path}/${id}` : null);

const ledger = (journalEntryId?: string | null, current = false): FlowStage => ({
    key: 'ledger', label: 'Ledger', permissions: [P.MENU_ACCOUNTING],
    refs: journalEntryId ? [{ text: 'Journal entry', to: at('/accounting/journal-entries', journalEntryId) }] : [],
    current,
});

// ---------------------------------------------------------------- Sales: Quotation > Order > Challan > Invoice > Receipt > Ledger

interface SalesFlowInput {
    current: 'quotation' | 'order' | 'challan' | 'invoice' | 'receipt';
    quotation?: { id?: string | null; no?: string | null };
    order?: { id?: string | null; no?: string | null };
    challans?: { id: string; no?: string | null }[];
    invoices?: { id?: string | null; no?: string | null }[];
    receipts?: { id: string; no?: string | null }[];
    journalEntryId?: string | null;
}

export function salesFlow(i: SalesFlowInput): FlowStage[] {
    const is = (k: SalesFlowInput['current']) => i.current === k;
    return [
        { key: 'quotation', label: 'Quotation', current: is('quotation'), permissions: [P.QUOTATION_VIEW],
          refs: ref(i.quotation?.no ?? (i.quotation?.id ? 'Quotation' : null), at('/sales/quotations', i.quotation?.id)) },
        { key: 'order', label: 'Order', current: is('order'), permissions: [P.SALES_ORDER_VIEW],
          refs: ref(i.order?.no ?? (i.order?.id ? 'Order' : null), at('/sales/orders', i.order?.id)) },
        { key: 'challan', label: 'Challan', current: is('challan'), permissions: [P.SALES_ORDER_VIEW],
          refs: (i.challans ?? []).map(c => ({ text: c.no ?? 'Challan', to: at('/sales/challans', c.id) })) },
        { key: 'invoice', label: 'Invoice', current: is('invoice'), permissions: [P.SALES_INVOICE_VIEW],
          refs: (i.invoices ?? []).flatMap(v => ref(v.no ?? (v.id ? 'Invoice' : null), at('/sales/invoices', v.id))) },
        { key: 'receipt', label: 'Receipt', current: is('receipt'),
          // the /sales/receipts/:id route sits inside the SALES_INVOICE_VIEW guard and the API needs RECEIPT_VIEW
          allKeys: [P.SALES_INVOICE_VIEW, P.RECEIPT_VIEW],
          refs: (i.receipts ?? []).map(r => ({ text: r.no ?? 'Receipt', to: at('/sales/receipts', r.id) })) },
        ledger(i.journalEntryId),
    ];
}

// ---------------------------------------------------------------- Purchasing: Requisition > PO > GRN > Bill > Payment

interface PurchaseFlowInput {
    current: 'requisition' | 'po' | 'grn' | 'bill' | 'payment';
    requisition?: { id?: string | null; no?: string | null };
    po?: { id?: string | null; no?: string | null };
    grn?: { id?: string | null; no?: string | null };
    bill?: { id?: string | null; no?: string | null };
    payment?: { id?: string | null; no?: string | null };
}

export function purchaseFlow(i: PurchaseFlowInput): FlowStage[] {
    const is = (k: PurchaseFlowInput['current']) => i.current === k;
    const one = (r: { id?: string | null; no?: string | null } | undefined, fallback: string, base: string) =>
        ref(r?.no ?? (r?.id ? fallback : null), at(base, r?.id));
    return [
        { key: 'requisition', label: 'Requisition', current: is('requisition'), permissions: REQ_VIEW,
          refs: one(i.requisition, 'Requisition', '/requisition') },
        { key: 'po', label: 'PO', current: is('po'), permissions: [P.PURCHASE_ORDER_VIEW],
          refs: one(i.po, 'Purchase order', '/procurement/purchase-orders') },
        { key: 'grn', label: 'GRN', current: is('grn'), permissions: [P.PURCHASE_ORDER_VIEW],
          refs: one(i.grn, 'Goods receipt', '/procurement/goods-receipts') },
        { key: 'bill', label: 'Bill', current: is('bill'), permissions: [P.SUPPLIER_BILL_VIEW],
          refs: one(i.bill, 'Bill', '/procurement/supplier-bills') },
        { key: 'payment', label: 'Payment', current: is('payment'), permissions: [P.SUPPLIER_PAYMENT_VIEW],
          refs: one(i.payment, 'Payment', '/procurement/supplier-payments') },
    ];
}

// ---------------------------------------------------------------- Commercial: LC > BTB > UD > EXP

interface LcFlowInput {
    current: 'lc' | 'btb' | 'ud' | 'exp';
    lc?: { id?: string | null; no?: string | null };
    /** Set only when the page knows a specific back-to-back LC (its own route carries both ids). */
    btb?: { id?: string | null; no?: string | null };
    btbHint?: string;
    ud?: { id?: string | null; no?: string | null };
    exp?: { id?: string | null; no?: string | null };
}

export function lcFlow(i: LcFlowInput): FlowStage[] {
    const is = (k: LcFlowInput['current']) => i.current === k;
    return [
        { key: 'lc', label: 'LC', current: is('lc'), permissions: [P.LC_VIEW], refs: ref(i.lc?.no ?? (i.lc?.id ? 'Export LC' : null), at('/commercial/lc', i.lc?.id)) },
        { key: 'btb', label: 'BTB', current: is('btb'), permissions: [P.LC_VIEW], hint: i.btbHint,
          refs: i.btb?.id && i.lc?.id ? ref(i.btb.no ?? 'Back-to-back LC', `/commercial/back-to-back-lc/${i.lc.id}/${i.btb.id}`) : ref(i.btb?.no, null) },
        { key: 'ud', label: 'UD', current: is('ud'), permissions: [P.UD_VIEW], refs: ref(i.ud?.no ?? (i.ud?.id ? 'UD' : null), at('/commercial/ud-register', i.ud?.id)) },
        { key: 'exp', label: 'EXP', current: is('exp'), permissions: [P.EXP_VIEW], refs: ref(i.exp?.no ?? (i.exp?.id ? 'EXP form' : null), at('/commercial/exp-form', i.exp?.id)) },
    ];
}

/** The distinct invoices of a list of challans (an order can have several challans on one invoice). */
export function invoicesOfChallans(challans: { salesInvoiceId?: string | null; invoiceNumber?: string | null }[] = []): { id: string; no?: string | null }[] {
    const seen = new Map<string, { id: string; no?: string | null }>();
    challans.forEach(c => { if (c.salesInvoiceId && !seen.has(c.salesInvoiceId)) seen.set(c.salesInvoiceId, { id: c.salesInvoiceId, no: c.invoiceNumber }); });
    return Array.from(seen.values());
}

// ---------------------------------------------------------------- Maintenance: Breakdown request > Work order > Parts > Close

interface MaintFlowInput {
    requestNo?: string | null;
    workOrderNo?: string | null;
    parts: { requisitionId?: string | null; ref?: string | null; raisedByMe?: boolean }[];
    closed: boolean;
}

export function maintenanceFlow(i: MaintFlowInput): FlowStage[] {
    return [
        { key: 'request', label: 'Breakdown request', muted: !i.requestNo, refs: ref(i.requestNo, null),
          hint: 'Breakdown requests have no page of their own. They are listed in the requests inbox.' },
        { key: 'workOrder', label: 'Work order', current: true, permissions: WO_VIEW, refs: ref(i.workOrderNo ?? 'This work order', null) },
        { key: 'parts', label: 'Parts', muted: i.parts.length === 0, permissions: REQ_VIEW,
          refs: i.parts.map((p, n) => ({ text: `#${n + 1}`, to: at('/requisition', p.requisitionId), own: p.raisedByMe })),
          hint: 'Parts are a section of this page. Each numbered link opens the store requisition that asked for the parts.' },
        { key: 'close', label: i.closed ? 'Closed' : 'Close', muted: !i.closed, hint: 'Closing is a status of this work order, not a separate page.' },
    ];
}
